// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  hasProjectNameRequiredError,
  hasUniqueViolationError,
} from '@/api/error-utils';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  PROJECT_NAME_MAX_LENGTH,
  PROJECT_NAME_MAX_LENGTH_MESSAGE,
  Project,
} from '@/project/api/types';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const NAME_REQUIRED_MESSAGE = 'Name is required';

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, NAME_REQUIRED_MESSAGE)
    .max(PROJECT_NAME_MAX_LENGTH, PROJECT_NAME_MAX_LENGTH_MESSAGE),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  project: Project;
  onClose: () => void;
}

export function ProjectNameDialog(props: Props) {
  const updateProjectMutation = useUpdateProject(props.project.key, {
    onSuccess: () => {
      props.onClose();
    },
    onError: (error) => {
      if (hasUniqueViolationError(error)) {
        setError('name', {
          type: 'custom',
          message: 'A project with this name already exists',
        });
      }

      if (hasProjectNameRequiredError(error)) {
        setError('name', { type: 'custom', message: NAME_REQUIRED_MESSAGE });
      }
    },
  });

  const { control, setError, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: props.project.name },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    updateProjectMutation.mutate({ name: data.name });
  };

  const isAlertOpen =
    updateProjectMutation.isError &&
    !hasUniqueViolationError(updateProjectMutation.error) &&
    !hasProjectNameRequiredError(updateProjectMutation.error);

  const close = () => {
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={isAlertOpen}
        message="An error occurred while updating the project."
        onClose={updateProjectMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Edit name</DialogTitle>
        <DialogContent dividers>
          <FormTextField
            fullWidth
            name="name"
            label="Name"
            control={control}
            disabled={updateProjectMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={close}
            disabled={updateProjectMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={updateProjectMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
